import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authorizeClient, client } from '../../networking/client';
import { RootState } from '../store';
import { toast } from 'react-toastify';
import { IDocument } from './documents';
import { AxiosResponse } from 'axios';

export interface IExamination {
  examination_name: string;
  examination_short_abbreviation: string;
  work_order_examination_id: string;
  attachments?: IDocument[];
  valueQuantity?: {
    value?: number;
    unit?: string;
  };
}

export interface ExaminationsState {
  status: string;
  examinations: IExamination[];
  customerExaminations: IExaminationByCustomer[];
}

type SingleExaminationInputType = {
  work_order_id: number;
  examination_id: number;
  higo_patient_id: string;
  medical_operator_id: number;
};

type CreateExaminationResponseType = {
  sp_external_examination_save: IExamination[];
};

const initialState: ExaminationsState = {
  status: 'idle',
  examinations: [],
  customerExaminations: [],
};

type ExaminationAttachments = {
  name: string;
  attachment_type_id: number;
  link: string;
  title: string;
  description: string;
  datetime_created: string;
  mime_type: string;
  file_extension: string;
  transcript?: string;
};

type Examinations = {
  examination_id: number;
  examination_name: string;
  examination_type: string;
  attachments: ExaminationAttachments[];
};
export interface IExaminationByCustomer {
  ta_id: number;
  product_order_id: number;
  product_order_item_id: number;
  product_id: number;
  product_name: string;
  record_state: string;
  merchant_account_number: string;
  merchant_name: string;
  doctor_name: string;
  examination_text: string;
  appointment_start: string;
  appointment_end: string;
  result_date: string;
  examinations: Examinations[];
}

export const fetchExaminationByCustomer = createAsyncThunk(
  'examinations/examinationsByCustomer',
  async (customerAccountNumber: string, { dispatch, rejectWithValue }) => {
    try {
      const examinationsResponse: AxiosResponse<IExaminationByCustomer[]> =
        await authorizeClient.get(
          `/core/merchant/${customerAccountNumber}/product_order/examinations`
        );
      return examinationsResponse;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const fetchExaminations = createAsyncThunk(
  'examinations/fetchAll',
  async (workOrderId: number, { dispatch, rejectWithValue }) => {
    try {
      const examinationsResponse: AxiosResponse<IExamination[]> = await client.get(
        `/healthpoint/work-order-examinations/${workOrderId}`
      );
      return examinationsResponse;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const fetchExaminationByID = createAsyncThunk(
  'examinations/fetchByID',
  async (workOrderExaminationId: string, { dispatch, rejectWithValue }) => {
    try {
      const examinationResponse: AxiosResponse<IExamination[]> = await client.get(
        `/healthpoint/examination-result/${workOrderExaminationId}`
      );
      return examinationResponse;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const startSingleExamination = createAsyncThunk(
  'examinations/start-single-exam',
  async (body: SingleExaminationInputType, { dispatch, rejectWithValue }) => {
    try {
      const examinationResponse: AxiosResponse<CreateExaminationResponseType[]> = await client.post(
        '/healthpoint/examination/',
        body
      );
      return examinationResponse;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const examinationsSlice = createSlice({
  name: 'examinations',
  initialState,
  reducers: {
    resetExaminations: (_) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExaminationByCustomer.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(fetchExaminationByCustomer.fulfilled, (state, action) => {
        state.status = 'fulfilled';
        state.customerExaminations = action.payload.data || [];
      })
      .addCase(fetchExaminationByCustomer.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.status = 'rejected';
      })
      .addCase(fetchExaminations.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(fetchExaminations.fulfilled, (state, action) => {
        // set examinations only if it not set with the correct work_order_examination_ids
        if (
          !state.examinations
            .filter((e) => e.work_order_examination_id)
            .some((stateExamination) =>
              action.payload.data
                .filter((e) => e.work_order_examination_id)
                .some(
                  (payloadExamination) =>
                    stateExamination.work_order_examination_id ===
                    payloadExamination.work_order_examination_id
                )
            )
        ) {
          state.examinations = action.payload.data.map((examination) => ({
            ...examination,
            attachments: undefined,
          }));
        }
        state.status = 'fulfilled';
      })
      .addCase(fetchExaminations.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.status = 'rejected';
      })
      .addCase(fetchExaminationByID.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(fetchExaminationByID.fulfilled, (state, action) => {
        const tempExam = state.examinations.find((e) => e.examination_name === 'Temperature');
        if (action.payload.data[0]?.attachments === null) {
          if (!tempExam) {
            toast.error('No attachments found', {
              position: toast.POSITION.BOTTOM_RIGHT,
            });
          }
        }
        const examinationIdx = state.examinations.findIndex(
          (examination) => examination.examination_name === action.payload.data[0]?.examination_name
        );
        if (examinationIdx !== -1 && action.payload.data) {
          state.examinations[examinationIdx] = {
            ...state.examinations[examinationIdx],
            ...action.payload.data[0],
          };
        }
        state.status = 'fulfilled';
      })
      .addCase(fetchExaminationByID.rejected, (state, action: any) => {
        const tempExam = state.examinations.find((e) => e.examination_name === 'Temperature');
        if (!tempExam) {
          toast.info('No attachments found', {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        }
        state.status = 'rejected';
      })

      .addCase(startSingleExamination.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(startSingleExamination.fulfilled, (state, action) => {
        const examinationIdx = state.examinations.findIndex(
          (examination) =>
            examination.examination_name ===
            action.payload.data[0]?.sp_external_examination_save[0]?.examination_name
        );
        if (examinationIdx !== -1 && action.payload.data) {
          state.examinations[examinationIdx] = {
            ...state.examinations[examinationIdx],
            ...action.payload.data[0]?.sp_external_examination_save[0],
          };
        }
        state.status = 'fulfilled';
      })
      .addCase(startSingleExamination.rejected, (state, action: any) => {
        console.log(action.payload);
        toast.error(action?.payload?.message ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.status = 'rejected';
      });
  },
});

export const { resetExaminations } = examinationsSlice.actions;

export default examinationsSlice.reducer;

export const selectExaminations = (state: RootState) => state.examinations.examinations;
export const selectExaminationsStatus = (state: RootState) => state.examinations.status;
export const selectCustomerExaminations = (state: RootState) =>
  state.examinations.customerExaminations;
